import { execute } from '@/graphql/execute';
import type { ImagesQuery } from '@/graphql/graphql';
import { imagesByLayerQuery } from '@/queries/image.query';
import { createOfflineStorage } from './createOfflineStorage';
import { getSnapshot } from './snapshotStore';

/** A single image (POI) as returned by `imagesByLayerQuery`. */
export type OfflineImage = ImagesQuery['imagesByLayer'][number];

/**
 * Load image-layer POIs for the given layers, falling back to the offline
 * snapshot (written at download time) when the network is unavailable. Mirrors
 * `loadFeaturesByLayerOfflineAware`. The preview photos themselves are served
 * from the `offlineres://` cache; this only provides the marker metadata.
 */
export async function loadImagesByLayerOfflineAware(
  layerIds: string[],
  projectId: string,
): Promise<OfflineImage[]> {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (!offline) {
    try {
      const data = await execute(imagesByLayerQuery, { layerIds });
      return data.imagesByLayer;
    } catch (err) {
      return loadFromSnapshot(layerIds, projectId, err);
    }
  }

  return loadFromSnapshot(layerIds, projectId, new Error('offline'));
}

async function loadFromSnapshot(
  layerIds: string[],
  projectId: string,
  originalError: unknown,
): Promise<OfflineImage[]> {
  const storage = await createOfflineStorage();
  const snap = await getSnapshot<OfflineImage[]>(storage, projectId, 'images');
  if (!snap) throw originalError;
  const wanted = new Set(layerIds);
  return snap.filter((img) => img.layerId != null && wanted.has(img.layerId));
}
